import type * as React from "react";
import { useTranslation } from "react-i18next";
import type { DocumentKind } from "@steelbill/types";
import { Badge } from "@/components/ui/badge";

/**
 * A document's status, as a word the owner reads (§11.1, §11.4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS EXISTS BECAUSE `{doc.status}` WAS BEING RENDERED RAW. The API sends the
 * Prisma enum verbatim — `ISSUED`, `CANCELLED`, `PARTIALLY_CONVERTED` — and
 * printing it puts a SCREAMING_SNAKE_CASE database identifier in front of a
 * 55-year-old fabricator who does not read English comfortably. §11.4 is
 * explicit: never hardcode a user-facing string in a component, and an enum
 * value rendered directly is the same defect wearing a different hat. The
 * `status.*` keys already existed in both locales; nothing was using them.
 *
 * STATUS IS NEVER COLOUR ALONE (§11.1). The badge always carries the word. The
 * variant is a second, redundant signal — for the owner in workshop daylight on
 * a phone screen, and for anyone who cannot separate the hues.
 *
 * UNKNOWN VALUES FALL BACK TO THE RAW STRING rather than to an empty badge. If
 * the API grows a status this map has not learned, a row reading `PENDING_IRN`
 * is ugly but honest; a blank badge would say the bill has no status at all.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * The four meanings a document status can carry, and their colours.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY EXPLICIT CLASSES AND NOT A `success` BADGE VARIANT. shadcn's neutral base
 * ships four variants — default, secondary, destructive, outline — and no green.
 * Adding one means editing `components/ui/badge.tsx`, which the shadcn CLI OWNS
 * and rewrites: `index.css` has already been regenerated several times in this
 * project and anything hand-added to it was lost. These live in the component
 * that uses them, where the CLI cannot reach.
 *
 * BOTH THEMES, DELIBERATELY. A `ThemeProvider` is now mounted, so dark is real
 * and a light-only green would be unreadable on it. Each tone names its dark
 * counterpart rather than relying on opacity, which is what turns a green badge
 * into a grey smear on a dark ground.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL (§11.1). Every badge still carries the WORD —
 * "Issued", "Cancelled" — so the meaning survives a monochrome print, a dusty
 * workshop screen and anyone who cannot separate red from green. The colour is a
 * second, redundant channel, which is the only kind worth having.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * EXPORTED so the drafts register can wear the same colours. A saved form has
 * its own two states (DRAFT / READY) that are not document statuses and must
 * never enter the map below — but they sit one table above documents on the
 * same screen, and a second amber invented over there would drift from this one
 * the first time either was touched.
 */
export const TONE = {
  /** Good, and done. */
  good: "border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300",
  /** Wrong, withdrawn, or refused — the ones that must stop the eye. */
  bad: "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300",
  /** In progress, or waiting on somebody else. Nothing is wrong yet. */
  waiting:
    "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
  /** Over, without a verdict. */
  spent: "border-border bg-muted text-muted-foreground",
} as const;

type Tone = keyof typeof TONE;

/**
 * Prisma enum → (translation key, tone).
 *
 * Covers InvoiceStatus and QuotationStatus together — the register, the recent
 * list and the document header all render "some document's status" and none of
 * them should own its own mapping.
 */
interface StatusEntry {
  key: string;
  tone: Tone;
}

const STATUS: Record<string, StatusEntry> = {
  /* InvoiceStatus (prisma/schema.prisma) */
  //
  // DRAFT IS AMBER, NOT RED. It was red briefly and that was wrong: red says
  // "something has gone wrong", and nothing has. A draft is a bill waiting for
  // its S.O. number (§7.1) — in progress, not broken — which is the same state
  // as a quotation waiting for a reply, and it should read the same way.
  //
  // Red is reserved for the two that ARE wrong: a cancelled bill and a voided
  // quotation. Keeping that meaning narrow is what makes it mean anything.
  DRAFT: { key: "status.draft", tone: "waiting" },
  PENDING_IRN: { key: "status.pendingIrn", tone: "waiting" },
  ISSUED: { key: "status.issued", tone: "good" },
  // A cancelled bill keeps its number and stays in the register (§17.4), so it
  // sits among valid ones and has to be impossible to mistake for one.
  CANCELLED: { key: "status.cancelled", tone: "bad" },

  /* QuotationStatus */
  SENT: { key: "status.sent", tone: "waiting" },
  APPROVED: { key: "status.approved", tone: "good" },
  REJECTED: { key: "status.rejected", tone: "bad" },
  // It became a bill — the outcome this document existed for.
  CONVERTED: { key: "status.converted", tone: "good" },
  PARTIALLY_CONVERTED: { key: "status.partiallyConverted", tone: "waiting" },
  /*
   * RED, NOT GREY (owner's call, 2026-08-16). It was `spent` — "over, without a
   * verdict" — on the reasoning that nothing went WRONG: the offer simply ran
   * out of time.
   *
   * That reads the document and not the business. An expired quotation is work
   * that was priced, sent, and then never answered — the customer went quiet and
   * the job was lost by default rather than by decision. Sat in grey beside
   * `Converted` and `Approved` it is the one outcome that looks like filing when
   * it is really a miss, and it is the only terminal state that could still have
   * gone the other way if somebody had rung them. It belongs with `Rejected`,
   * which is the same loss arrived at honestly.
   *
   * `bad` is defined as "wrong, withdrawn, or refused — the ones that must stop
   * the eye", and stopping the eye is exactly the point.
   */
  EXPIRED: { key: "status.expired", tone: "bad" },
  VOID: { key: "status.cancelled", tone: "bad" },

  /* ChallanStatus (§17.7, Rule 55 CGST) */
  //
  // A CHALLAN IS NOT A TAX DOCUMENT, so none of these touch any liability —
  // they record where the goods physically are. `CANCELLED` is deliberately not
  // repeated: it already sits above under InvoiceStatus with the same key and
  // the same tone, and two entries for one enum value is exactly the drift a
  // forked ChallanStatusBadge would have introduced.
  //
  // OPEN is amber for the same reason DRAFT is: nothing is wrong. The goods are
  // out at the galvaniser, or delivered with the bill still to write. The one
  // OPEN challan that IS a problem — expected back, day gone — is marked
  // separately by the register's overdue chip, because "late" is a fact about
  // the RETURN DATE and not about the status.
  OPEN: { key: "status.open", tone: "waiting" },
  // The movement closed the way it was meant to: the goods came back.
  RETURNED: { key: "status.returned", tone: "good" },
  // A tax invoice was raised against this movement, which is the outcome a
  // SUPPLY_NOT_INVOICED challan exists to be chased into.
  INVOICED: { key: "status.invoiced", tone: "good" },
};

/**
 * Which document is being described. `DRAFT` means different things to each.
 *
 * `challan` IS IN THE UNION THOUGH IT OVERRIDES NOTHING, and that is the guard
 * working rather than dead weight: {@link KIND_OVERRIDES} is a `Record` over
 * this union, so widening the type forced the entry below to be written and
 * considered. Without the widening, `kind="challan"` was a compile error and
 * passing no kind rendered the raw `OPEN` in the badge.
 *
 * IT IS AN ALIAS OF THE SHARED `DocumentKind` AS OF 2026-08-16, not a third
 * hand-typed copy of the same three words. It was widened by hand when challans
 * shipped and a forked `ChallanStatusBadge` in ChallanDetailScreen.tsx was left
 * behind citing the OLD, narrower version of this line — a stale duplicate
 * justified by a stale comment. Aliasing means §17.3's credit note arrives here
 * as a compile error in `KIND_OVERRIDES` and must be answered, the way
 * `challan: {}` was.
 */
export type StatusDocumentKind = DocumentKind;

/**
 * `DRAFT` RESOLVED PER KIND, because the word meant three things at once.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * Until 2026-08-15 the quotation register showed `DRAFT/QUOT/9` in its
 * unfinished-work strip and, one inch below, a numbered quotation whose status
 * badge also said "Draft" — same word, same amber, and consequences that are not
 * remotely the same:
 *
 *   DRAFT/QUOT/9   a saved FORM. No number. Throw it away and nothing happened.
 *   QOT/543/26     a real quotation. Its number is SPENT out of the gap-free
 *                  series (§6); abandoning it means VOIDING it, and the void
 *                  stays in the book for ever.
 *
 * Only the first is a draft in the sense anyone means it. The other two — a
 * quotation not yet sent, an invoice waiting for its S.O. number (§7.1) — are
 * FINISHED DOCUMENTS in a pre-release state, and each has its own reason.
 *
 * So the word "Draft" now belongs to the numberless form alone, and these say
 * what they actually mean. THE DATABASE IS UNCHANGED: the enum value is still
 * `DRAFT` in both tables, and nothing here is a migration — this is a label.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const KIND_OVERRIDES: Record<
  StatusDocumentKind,
  Record<string, StatusEntry>
> = {
  // §7.1 — converted from a quotation before the customer's S.O. number was
  // known. It holds a number and is waiting on one fact.
  invoice: { DRAFT: { key: "status.awaitingSo", tone: "waiting" } },
  // §7 — saved, numbered, and not yet gone to the customer.
  quotation: { DRAFT: { key: "status.notSent", tone: "waiting" } },
  /*
   * §17.7 — NOTHING TO OVERRIDE, and the empty object is the answer rather than
   * an omission. `ChallanStatus` has no DRAFT: a challan is numbered the moment
   * it is saved, because the goods are on a lorry and Rule 55 requires the paper
   * to travel with them. An unfinished challan is a `document_drafts` row with
   * no number at all, which the DraftsStrip shows and this badge never sees.
   */
  challan: {},
  /*
   * §17.3 — NOTHING TO OVERRIDE EITHER, and again the empty object is the
   * answer rather than an omission.
   *
   * `CreditNoteStatus` is `ISSUED | CANCELLED` and has no DRAFT: the note is
   * short, it is typed and issued in one sitting, and there is no
   * `document_drafts` row for one (`DraftKind` is a DB enum with no member for
   * it). Both of its states are already in the shared table above with the right
   * words and the right tones — ISSUED green, CANCELLED red — and a cancelled
   * credit note must stop the eye for a reason peculiar to this document: it
   * credits NOTHING, so the money it took off the bill has gone back on.
   */
  creditNote: {},
};

function resolve(
  status: string,
  kind?: StatusDocumentKind,
): StatusEntry | undefined {
  // Kind-specific first, then the shared table. A caller that cannot say which
  // kind it holds still gets the generic label rather than nothing.
  return (
    (kind === undefined ? undefined : KIND_OVERRIDES[kind][status]) ??
    STATUS[status]
  );
}

export function DocumentStatusBadge({
  status,
  kind,
  className,
}: {
  status: string;
  /** Omit only where the kind genuinely is not known — `DRAFT` then reads generically. */
  kind?: StatusDocumentKind;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const entry = resolve(status, kind);

  return (
    <Badge
      /*
       * `outline` is the base because it is the only variant that sets no
       * background or text colour of its own — anything else would fight the
       * tone classes and win on some of them, leaving one badge the right colour
       * and the next one grey.
       */
      variant="outline"
      className={`${entry === undefined ? "" : TONE[entry.tone]} ${className ?? ""}`}
    >
      {entry === undefined ? status : t(entry.key)}
    </Badge>
  );
}

/** The status label on its own, for an `aria-label` or a tooltip. */
export function useStatusLabel(): (
  status: string,
  kind?: StatusDocumentKind,
) => string {
  const { t } = useTranslation();
  return (status: string, kind?: StatusDocumentKind) => {
    const entry = resolve(status, kind);
    return entry === undefined ? status : t(entry.key);
  };
}

/**
 * The same four tones, for a SQUARE ICON TILE rather than a pill.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY A SECOND MAP AND NOT `TONE` ITSELF. `TONE`'s entries carry a `border-*`
 * colour, which `Badge` renders because its own base class sets a border width.
 * An icon tile sets none, so pasting `TONE` there would drag along a colour that
 * silently does nothing — right today, and quietly wrong the moment anyone gives
 * the tile a border. Keyed by the same `Tone` union, so the two cannot fall out
 * of step: adding a tone breaks this map at compile time.
 *
 * The pairs are the SAME background and foreground `TONE` uses, so a row's tile
 * and its badge are the same colour rather than two greens that nearly match.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const ICON_TONE: Record<Tone, string> = {
  good: "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-300",
  bad: "bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-300",
  waiting: "bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  spent: "bg-muted text-muted-foreground",
};

/**
 * The icon-tile classes for a document's status — the tile matching the badge
 * beside it (owner's ask, 2026-08-16).
 *
 * Falls back to the neutral tile for a status this file does not know, which is
 * the same thing the badge does: it prints the raw status rather than guessing a
 * colour, and an unknown state must not be dressed as a good one.
 */
export function statusIconTone(
  status: string,
  kind?: StatusDocumentKind,
): string {
  const entry = resolve(status, kind);
  return entry === undefined ? ICON_TONE.spent : ICON_TONE[entry.tone];
}
